<template>
  <div id="membership">
    <v-container>
      <v-layout>
        <v-flex class="px-2 flex-column">
          <p class="pf-font-18">Your membership: <b>{{ Account.membership }}</b></p>
          
          <template v-if="isAccountSuspended">
            <p class="pf-font-15 pf-text-warning">Your account seems to be suspended due to a non-payment for the membership. Below are the details. You can either add a new credit card or process the payment with an existing card to continue using Parentfinder services.</p>
  
            <v-flex v-if="transactionList.length">
              <v-data-table
                :headers="headers"
                :items="transactionList"
                class="pf-data-table"
                :pagination.sync="pagination"
              >
                <template v-slot:items="props">
                  <tr>
                    <td>{{ formatDate(props.item.submit_date, 'YYYY-MM-DD') }}</td>
                    <td>{{ props.item.subscription ? props.item.subscription.id : '--' }}</td>
                    <td>
                      <template v-if="props.item.subscription && props.item.subscription.name">
                        {{ props.item.subscription.name }}
                      </template>
                      <template v-else>--</template>
                    </td>
                    <td>{{ props.item.transaction_id }}</td>
                    <td class="pf-text-red">{{ statusTexts[props.item.transaction_status] ? statusTexts[props.item.transaction_status] : props.item.transaction_status }}</td>
                    <td>{{ props.item.payment_amount }}</td>
                    <td align="right">
                      <v-flex class="flex-space-between">
                        <v-btn v-if="props.item && props.item.account" icon class="ma-0" @click="changePaymentMethod(props.item)" title="Change payment method">
                          <v-icon size="22" color="grey darken-2">payments</v-icon>
                        </v-btn>
                      </v-flex>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-flex>
            <v-flex v-else>
              <p>No declined transaction found. Please contact with your agency</p>
            </v-flex>
          </template>

          <v-layout v-else class="flex-column">
            <v-flex v-if="subscription && subscription.status === 'Subscribed'">
              <v-btn class="pf-btn pf-bg-teal" @click="cancelMembership">Cancel Subscription</v-btn>
            </v-flex>
            <v-flex v-else-if="subscription && ['Cancelled', 'Expired', 'Terminated', 'Suspended'].indexOf(subscription.status) > -1">
              <p v-if="subscription.status === 'Cancelled'">Your membership was cancelled{{ subscription.cancelled_date ? ` on ${formatDate(subscription.cancelled_date)}` : '' }}</p>
              <p v-else-if="subscription.status === 'Terminated'">Your membership was terminated</p>
              <p v-else-if="subscription.status === 'Expired'">Your membership was expired</p>
              <p v-else-if="subscription.status === 'Suspended'">Your membership was suspended</p>
              <v-btn class="pf-btn pf-bg-teal" @click="renewMembership">Renew Membership</v-btn>
            </v-flex>
          </v-layout>
          
        </v-flex>
      </v-layout>
    </v-container>

    <app-modal :active="showAddCardPopup" v-if="showAddCardPopup" width="30%">
      <div slot="modal-content">
        <v-card>
          <v-toolbar card dark class="pf-bg-teal">
            <v-btn class="pf-modal-close" icon dark @click="closeAddCardPopup">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>Add New Card</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="mt-4 mb-4">
            <v-form ref="newCardForm">
              <v-layout wrap>
                <!-- Content -->
                <v-flex xs12>
                  <v-text-field label="Card number" v-model.trim="newCardNumber" :rules="rules.card.number" class="required pr-3 pl-3 flex-center" @input="newCardChange($event)" placeholder="#### #### #### ####" :value="newCard.number" v-mask="'#### #### #### ####'" type="password">
                    <template v-slot:prepend>
                      <v-fade-transition leave-absolute>
                        <div class="pf-card-icon-single" :style="`background-image:url(/static/cards/${newCard.icon});`"></div>
                      </v-fade-transition>
                    </template>
                  </v-text-field>
                  <span v-if="newCardNumberMask" class="pf-pos-relative tm-20 ml-3">Card Number: {{ newCardNumberMask }}</span>
                </v-flex>
                <v-flex xs12>
                  <v-text-field v-model.trim="newCard.name" :rules="rules.card.name" label="Card holder name" class="required pr-3 pl-3"></v-text-field>
                </v-flex>
                <v-flex xs8>
                  <v-text-field v-model.trim="newCard.expiry" :rules="rules.card.expiry" label="Expiry date" class="required pr-3 pl-3" v-mask="'## / ##'" placeholder="MM/YY"></v-text-field>
                </v-flex>
                <v-flex xs4>
                  <v-text-field v-model.trim="dummyCvv" :rules="rules.card.cvv" label="CVV" class="required pr-3 pl-3" v-mask="'####'" type="password" placeholder="CVV"></v-text-field>
                </v-flex>
              </v-layout>
            </v-form>

          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="pf-btn-text-white pf-bg-green"
              @click="saveNewCard"
            >
              Add Card
            </v-btn>
            <v-btn
              class="pf-btn-text-white pf-bg-pink"
              @click="closeAddCardPopup"
            >
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </app-modal>

    <app-modal :active="isChangePaymentMethod" v-if="isChangePaymentMethod" width="40%">
      <div slot="modal-content">
        <v-card>
          <v-toolbar card dark class="pf-bg-teal">
            <v-btn class="pf-modal-close" icon dark @click="closeChangePaymentMethod">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>Change Payment Method</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="mt-1 mb-2 pa-4">
            <p class="pf-text-default">Choose your saved cards or add a new one.</p>
            <p class="pf-text-warning">We'll try to process the declined payment while you submit.</p>
            <!-- List cards -->
            <v-layout>
              <v-flex class="flex-column">
                <v-radio-group v-model="selectedCardId" @change="{}" return-object>
                  <p class="ml-2" v-if="currentTransaction && currentTransaction.profile && currentTransaction.profile.paymentProfile.payment && currentTransaction.profile.paymentProfile.payment.creditCard">{{ currentTransaction.profile.paymentProfile.payment.creditCard.cardNumber }} <small>(selected)</small></p>
                  <p v-else-if="!cards.length">No saved cards found.</p>
                  
                  <v-layout v-for="(card, i) in cards" :key="i">
                    <v-flex class="flex-center">
                      <v-radio
                        :value="card.id"
                        class="flex-center"
                      >
                        <template v-slot:label>
                          <div class="flex-center">
                            <div class="pf-card-icon" :style="`background-image:url(/static/cards/${card.icon});`"></div>
                            #### #### #### {{ card.number.substr(card.number.length - 4) }}
                          </div>
                        </template>
                      </v-radio>
                    </v-flex>
                  </v-layout>
                </v-radio-group>
                <v-btn class="pf-btn pf-btn-save pf-width-50p" @click="addNewCard">
                  <v-icon size="16" class="pr-1">add</v-icon>
                  <div>Add New Card</div>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="pf-btn-text-white pf-bg-green"
              @click="saveNewPaymentMethod"
              :disabled="!selectedCardId"
            >
              Submit
            </v-btn>
            <v-btn
              class="pf-btn-text-white pf-bg-pink"
              @click="closeChangePaymentMethod"
            >
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </app-modal>

    <!-- Renewal -->
    <app-modal :active="isChoosePaymentMethod" v-if="isChoosePaymentMethod" width="40%">
      <div slot="modal-content">
        <v-card>
          <v-toolbar card dark class="pf-bg-teal">
            <v-btn class="pf-modal-close" icon dark @click="closeChoosePaymentMethod">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>Select a Payment Method</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="mt-1 mb-2 pa-4">
            <p class="pf-text-default">Choose your saved cards or add a new one.</p>
            <!-- List cards -->
            <v-layout>
              <v-flex class="flex-column">
                <v-radio-group v-model="selectedCardId" @change="{}" return-object>
                  <p v-if="!cards.length">No saved cards found.</p>
                  <v-layout v-for="(card, i) in cards" :key="i">
                    <v-flex class="flex-center">
                      <v-radio
                        :value="card.id"
                        class="flex-center"
                      >
                        <template v-slot:label>
                          <div class="flex-center">
                            <div class="pf-card-icon" :style="`background-image:url(/static/cards/${card.icon});`"></div>
                            #### #### #### {{ card.number.substr(card.number.length - 4) }}
                          </div>
                        </template>
                      </v-radio>
                    </v-flex>
                  </v-layout>
                </v-radio-group>
                <v-btn class="pf-btn pf-btn-save pf-width-50p" @click="addNewCard(true)">
                  <v-icon size="16" class="pr-1">add</v-icon>
                  <div>Add New Card</div>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="pf-btn-text-white pf-bg-green"
              @click="renewMembershipWithCard"
              :disabled="!selectedCardId"
            >
              Submit
            </v-btn>
            <v-btn
              class="pf-btn-text-white pf-bg-pink"
              @click="closeChoosePaymentMethod"
            >
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </app-modal>
  </div>
</template>
<script>

/* global config session store */
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { getCollection, formatDate, create, request, getCardType } from '../helpers/helpers';
import swal from 'sweetalert2';
import AppModal from '../components/AppModal.vue';
import VueClipboard from 'vue-clipboard2';
import { mask } from 'vue-the-mask';
Vue.use(VueClipboard);

const sleep = (milliseconds) => {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
};

export default {
  name: 'membership',
  directives: { mask },
  components: {
    'app-modal': AppModal
  },
  data: (vm) => ({
    transactionList: [],
    subscription: null,
    headers: [
      { text: 'Date', value: 'submit_date', align: 'left' },
      { text: 'Subscr ID', value: 'subscription.id', align: 'left' },
      { text: 'Subscr Name', value: 'subscription.name' },
      { text: 'Trans ID', value: 'transaction_id' },
      { text: 'Status', value: 'transaction_status' },
      { text: 'Amount', value: 'payment_amount' },
      { text: 'Actions', sortable: false }
    ],
    pagination: {
      rowsPerPage: config.dom.rowsPerPage,
      sortBy: ''
    },
    statusTexts: {
      settledSuccessfully: 'Success Transaction',
      voided: 'Voided Transaction',
      expired: 'Expired',
      declined: 'Declined',
      pendingFinalSettlement: 'Pending',
      generalError: 'General Error',
      communicationError: 'Communication Error',
      refundSettledSuccessfully: 'Refund Success',
    },
    rules: {
      card: {
        number: [],
        name: [v => !!v || 'Card holder name is required'],
        cvv: [v => !!v || 'CVV is required'],
        expiry: [
          v => !!v || 'Card expiry is required',
          v => vm.validateDateChange(v) || 'Date is invalid or card expired'
        ],
      }
    },
    isChangePaymentMethod: false,
    isChoosePaymentMethod: false,
    isRenew: false,
    newCard: {
      name: '',
      number: '',
      expiry: '',
      icon: 'credit-card.ico',
      type: ''
    },
    showAddCardPopup: false,
    dummyCvv: '',
    newCardNumber: '',
    newCardNumberMask: '',
    cardIcons: [
      { type: 'Visa', icon: 'visa.png' },
      { type: 'Mastercard', icon: 'mastercard.png' },
      { type: 'American Express', icon: 'amex.png' },
      { type: 'Diners Club', icon: 'diners-club.png' },
      { type: 'Discover', icon: 'discover.jpg' },
      { type: 'JCB', icon: 'jcb.png' },
      { type: 'Unknown', icon: 'credit-card.ico' }
    ],
    cards: [],
    selectedCardId: null,
  }),
  beforeRouteEnter (to, from, next) {
    if (!session.isOnline()) {
      next({ path: '/login' });
    } else {
      next();
    }
  },
  computed: {
    ...mapGetters(['Account']),
    isAccountSuspended () {
      return this.Account.agency.findIndex(agency => agency.status === 'Suspended') > -1;
    },
    PaymentMode: {
      get () {
        return store.state.PaymentMode;
      },
      set (value) {
        store.commit('setPaymentMode', value);
      }
    },
  },
  async created () {
    await this.getMembershipSubscription();
    await this.getDeclinedTransactions();
  },
  methods: {

    async getDeclinedTransactions () {

      let where = {
        account: this.Account.id,
        transaction_status: 'declined',
        $or: [{ retry_success: { $exists: false }}, { retry_success: false }]
      };
      let populate = [{ path: 'agency', select: 'name' }];

      let { error, data } = await getCollection('AuthorizeNetTransactionReport', true, 0, populate, where, null, null, { 'submit_date': 'desc' });

      if (error) {
        console.log(error);
        swal.fire({
          type: 'warning',
          title: 'Failed to Fetch',
          text: `Failed to fetch the transactions, please tray again.`,
          showConfirmButton: true,
          onClose: () => {}
        });
      } else {
        this.transactionList = data;
      }
    },
    async getMembershipSubscription () {

      let where = {
        account: this.Account.id,
        type: 'Membership'
      };
      let { error, data } = await getCollection('PaymentSubscription', false, 1, '', where, null, null, { 'createdAt': 'desc' });

      if (error) {
        console.log(error);
      } else if (data && data.length) {
        this.subscription = data[0];
      }
    },
    cancelMembership () {
      if (!this.subscription) {
        return;
      }
      swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure that you want to cancel the membership subscription?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, cancel it!'
      }).then(async (result) => {
        if (result.value) {
          (async () => {
            let payload = {
              account: this.subscription.account,
              subscription_type: 'Membership',
              subscription_id: parseInt(this.subscription.subscription_id, 10),
              id: this.subscription.id
            };
            let { error, data } = await request('PaymentSubscription/Cancel', 'PUT', payload);
            if (error) {
              console.log('error', error);
              swal.fire({
                type: 'warning',
                title: 'Error',
                text: typeof error === 'string' ? error : (error.message || 'Something went wrong, please check with your agency'),
                showConfirmButton: true,
                onClose: () => {}
              });
              return;
            } else {
              this.subscription.status = 'Cancelled';
              swal.fire({
                type: 'success',
                title: 'Success',
                text: 'The subscription was cancelled successfully',
                showConfirmButton: false,
                timer: 2500,
                onClose: () => {
                  // 
                }
              });
            }
          })();
        }
      });
    },
    async renewMembership () {
      if (this.subscription) {
        await this.fetchAccountPaymentModes(this.subscription.account);
      }
    },
    closeChoosePaymentMethod () {
      this.isChoosePaymentMethod = false;
      this.selectedCardId = null;
    },
    async renewMembershipWithCard () {
      let index = this.cards.findIndex(x => x.id === this.selectedCardId);
      if (this.selectedCardId && index > -1) {
        let payload = {
          subscription_id: parseInt(this.subscription.subscription_id, 10),
          payment_mode: this.selectedCardId
        };
        let { error, data } = await request('PaymentSubscription/Renew', 'POST', payload);
        if (error) {
          console.log(error);
          swal.fire({
            type: 'warning',
            title: 'Error',
            text: `${error.message || error}. If problem persists please contact support at support@parentfinder.com`,
            showConfirmButton: true
          });
        } else {
          this.closeChoosePaymentMethod();
          this.subscription = data;
          swal.fire({
            type: 'success',
            title: 'Your membership subscription was renewed successfully.',
            showConfirmButton: false,
            timer: 2500
          });
        }
      }
    },
    formatDate (dateString) {
      if (dateString) {
        return formatDate(dateString, 'MM/DD/YYYY');
      }
    },
    async fetchAccountPaymentModes (accountId) {
      if (accountId) {
        let where = { account: accountId };
        let { error, data } = await getCollection('PaymentMode', true, 0, '', where);
        if (error) {
          console.log(error);
        } else {
          if (data.length) {
            this.PaymentMode = data;
            /* Set the local card variable */
            this.setCards();
            this.isChoosePaymentMethod = true;
          } else {
            this.addNewCard(true);
          }
        }
      }
    },
    async fetchPaymentModes (item) {
      if (item && item.account && item.profile && item.profile.customerProfileId) {
        let where = { account: item.account, customer_profile_id: item.profile.customerProfileId };
        let { error, data } = await getCollection('PaymentMode', true, 0, '', where);
        if (error) {
          console.log(error);
        } else {
          this.PaymentMode = data;
          /* Set the local card variable */
          this.setCards();
        }
      } else {
        this.setCards();
      }
    },
    setCards () {
      this.cards = this.PaymentMode.map(mode => {
        mode.card.icon = 'credit-card.ico';
        mode.card.id = mode.id;
        return mode.card;
      });
    },
    async changePaymentMethod (item) {
      if (!item || !item.account) {
        return false;
      }
      if (!item.profile || !item.profile.customerProfileId) {
        swal.fire({
          type: 'warning',
          title: 'Warning',
          text: `The AuthorizeNet customer profile not found.`,
          showConfirmButton: true,
          onClose: () => {}
        });
        return false;
      }
      if (!item.subscription) {
        swal.fire({
          type: 'warning',
          title: 'Warning',
          text: `The AuthorizeNet payment subscription id not found.`,
          showConfirmButton: true,
          onClose: () => {}
        });
        return false;
      }
      /* Fetch the payment modes - Cards */
      await this.fetchPaymentModes(item);
  
      /* Set the card icon based on card type */
      this.setCardIcons();

      this.currentTransaction = item;
      this.isChangePaymentMethod = true;
    },
    setCardIcons () {
      this.cards = this.cards.map((x) => {
        let index = this.cardIcons.findIndex(y => y.type === x.type);
        if (x.type && index > -1) {
          x.icon = this.cardIcons[index].icon;
        }
        return x;
      });
    },
    closeChangePaymentMethod (clearCurrentTransaction = true) {
      if (clearCurrentTransaction) {
        this.currentTransaction = null;
      }
      this.isChangePaymentMethod = false;
      this.selectedCardId = null;
    },
    addNewCard (isRenew = false) {
      /* Close the popup if requested from settings */
      if (this.isChangePaymentMethod) {
        this.closeChangePaymentMethod(false);
      }
      this.dummyCvv = '';
      this.newCard = {
        name: '',
        number: '',
        expiry: '',
        icon: 'credit-card.ico',
        type: ''
      };
      this.showAddCardPopup = true;
      this.isRenew = isRenew;
    },
    closeAddCardPopup () {
      this.currentTransaction = null;
      this.showAddCardPopup = false;
      this.newCardNumber = '';
      this.newCardNumberMask = '';
      this.newCard = {
        name: '',
        number: '',
        expiry: '',
        icon: 'credit-card.ico',
        type: ''
      };
      /* Open the change payment method popup if request comes from there */
      if (!this.isOverviewActive) {
        this.changePaymentMethod();
      } else {
        this.renewMembership();
      }
    },
    newCardChange (newValue) {
      this.newCard.number = newValue.replace(/ /g, '');
      if (this.newCard.number && this.newCard.number.trim() && this.newCard.number.replace(' ', '')) {
        let cardType = getCardType(this.newCard.number.replace(' ', ''));
        let index = this.cardIcons.findIndex(y => y.type === cardType);
        if (index > -1) {
          this.newCard.icon = this.cardIcons[index].icon;
          this.newCard.type = cardType;
        }
      }
      this.newCardNumberMask = this.newCard.number.replace(' ', '').replace(/.(?=.{4})/g, '#').match(/.{1,4}/g).join(' ');
    },
    async saveNewCard () {
      if (this.$refs.newCardForm.validate()) {
        if (this.newCard) {
          let payload = { card: JSON.parse(JSON.stringify(this.newCard)) };
          delete payload.card.icon;
          if (this.currentTransaction && this.currentTransaction.account) {
            payload.account = this.currentTransaction.account;
            payload.customer_profile_id = this.currentTransaction.profile.customerProfileId;
          } else {
            payload.account = this.Account.id;
          }
          /* format the card number and expiry */
          payload.card.number = payload.card.number.replace(/ /g, '');
          payload.card.expiry = payload.card.expiry.replace(' / ', '');
          payload.card.type = payload.card.type ? payload.card.type : 'Unknown';
          let { error, data } = await create('PaymentMode', payload);
          if (error) {
            console.log(error);
            this.newCard = {
              name: '',
              number: '',
              expiry: '',
              icon: 'credit-card.ico',
              type: ''
            };
            swal.fire({
              type: 'warning',
              title: 'Error',
              text: `${error.message || error}. If problem persists please contact support at support@parentfinder.com`,
              showConfirmButton: true
            });
          } else if (data) {
            this.PaymentMode.push(data);
            this.setCards();
            this.setCardIcons();
            
            this.newCard = {
              name: '',
              number: '',
              expiry: '',
              icon: 'credit-card.ico',
              type: ''
            };
            this.showAddCardPopup = false;
            
            if (!this.isRenew) {
              /* Ask the user if the new card should be selected as the payment method */
              swal.fire({
                title: 'Set Payment Method?',
                text: 'Your card was added successfully, do you want to set this new card as your default payment method?',
                type: 'success',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, I do!',
                cancelButtonText: 'No'
              }).then(async (result) => {
                if (result.value) {
                  this.selectedCardId = data.id;
                  /* Sleep for 2 seconds */
                  await sleep(2000);
                  /* Call the change payment method API */
                  await this.saveNewPaymentMethod();
                  this.currentTransaction = null;
                } else {
                  /* Open the change payment method popup again */
                  this.isChangePaymentMethod = true;
                }
              });
            } else {
              /* Ask the user if the new card should be used to proceed with the renewal */
              swal.fire({
                title: 'Use New Card?',
                text: 'Your card was added successfully, do you want to use this new card to proceed with the membership subscription?',
                type: 'success',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, I do!',
                cancelButtonText: 'No'
              }).then(async (result) => {
                if (result.value) {
                  this.selectedCardId = data.id;
                  /* Sleep for 2 seconds */
                  await sleep(2000);
                  /* Call the change payment method API */
                  await this.renewMembershipWithCard();
                } else {
                  /* Open the change payment method popup again */
                  this.isChoosePaymentMethod = true;
                }
              });
            }
          }
        }
      }
    },
    async saveNewPaymentMethod () {
      let index = this.cards.findIndex(x => x.id === this.selectedCardId);
      if (this.selectedCardId && index > -1 && this.currentTransaction && this.currentTransaction.profile && this.currentTransaction.subscription) {
        let payload = {
          authorize_profile_id: this.currentTransaction.profile.customerProfileId.toString(),
          subscription_id: this.currentTransaction.subscription.id.toString(),
          payment_mode: this.selectedCardId,
          request_from: 'TransactionReport'
        };
        let { error, data } = await request('PaymentSubscription/UpdatePaymentMode', 'PUT', payload);
        if (error) {
          console.log(error);
          swal.fire({
            type: 'warning',
            title: 'Error',
            text: `${error.message || error}. If problem persists please contact support at support@parentfinder.com`,
            showConfirmButton: true,
            onClose: () => {
              // 
            }
          });
        } else {
          this.closeChangePaymentMethod();

          swal.fire({
            type: 'success',
            title: 'Your pending payment(s) completed, you should be able to access your Parentfinder account now',
            showConfirmButton: false,
            timer: 2500,
            onClose: () => {
              // 
            }
          });
        }
      }
    },
    validateDateChange (value) {
      if (value && value.trim()) {
        let splited = this.newCard.expiry.trim().split(' / ');
        if (parseInt(splited[0], 10) > 12 || parseInt(splited[0], 10) < 1) {
          return false;
        }
        let currentYear = (new Date()).getFullYear();
        let currentMonth = (new Date()).getMonth() + 1;
        let lastTwo = currentYear.toString().substr(2);
        if (parseInt(splited[1], 10) < lastTwo || (parseInt(splited[1], 10) === lastTwo && currentMonth)) {
          return false;
        }
      }
      return true;
    },
  }
};

</script>
  
  
<style scoped>

  .v-btn--icon {
    border-radius: 0;
  }
  .icon-color {
      color: #fff !important;
  }
  .border {
    border: 1px solid grey;
  }
  @media(max-width:410px){
    .pf-btn{
      min-width: 85px;
    }
  }

</style>
  


// WEBPACK FOOTER //
// src/pages/Membership.vue